import { cn } from '@sohanemon/utils';
import { Iconify } from '@sohanemon/utils/components';
import { CodeBlockFrame } from '@sohanscript/lib/code/code-block';
import { CopyButton } from '@sohanscript/ui/copy-button';
import { MacosAction } from '@sohanscript/ui/macos-action';
import {
	type ComponentProps,
	isValidElement,
	type ReactNode,
	useMemo,
	useState,
} from 'react';
import { Pre } from './code-block';
import { FileTab } from './file-tab';
import type { FileEntry } from './lib/parse-file-entries';
import { getCodeText, parseFileEntries } from './lib/parse-file-entries';

type PreProps = ComponentProps<typeof Pre>;

// NOTE: the tabs and the editor re-render the code block inside their own chrome,
// so the parsed element's props have to travel with it. Dropping them loses the
// fence meta, which is where filename, icon and highlighted lines come from.
function elementProps(node: ReactNode): Partial<PreProps> {
	return isValidElement<PreProps>(node) ? node.props : {};
}

function elementChildren(node: ReactNode): ReactNode {
	return isValidElement<PreProps>(node) ? node.props.children : null;
}

type TreeNode = {
	name: string;
	path: string;
	children: Record<string, TreeNode>;
	file?: FileEntry;
};

function buildTree(files: FileEntry[]): TreeNode {
	const root: TreeNode = { name: '', path: '', children: {} };
	for (const f of files) {
		const parts = f.path.split('/').filter(Boolean);
		let cur = root;
		parts.forEach((part, idx) => {
			const isLast = idx === parts.length - 1;
			if (!cur.children[part]) {
				cur.children[part] = {
					name: part,
					path: parts.slice(0, idx + 1).join('/'),
					children: {},
				};
			}
			cur = cur.children[part];
			if (isLast) cur.file = f;
		});
	}
	return root;
}

function Tree({
	node,
	activePath,
	onSelect,
	depth = 0,
}: {
	node: TreeNode;
	activePath: string;
	onSelect: (path: string) => void;
	depth?: number;
}) {
	const entries = Object.values(node.children).sort((a, b) => {
		const aIsFolder = !a.file;
		const bIsFolder = !b.file;
		if (aIsFolder !== bIsFolder) return aIsFolder ? -1 : 1;
		return a.name.localeCompare(b.name);
	});

	return (
		<div className="space-y-0.5">
			{entries.map((child) => (
				<TreeItem
					key={child.path}
					node={child}
					activePath={activePath}
					onSelect={onSelect}
					depth={depth}
				/>
			))}
		</div>
	);
}

function TreeItem({
	node,
	activePath,
	onSelect,
	depth,
}: {
	node: TreeNode;
	activePath: string;
	onSelect: (path: string) => void;
	depth: number;
}) {
	const isFile = !!node.file;
	const [open, setOpen] = useState(true);
	// @icon
	const iconName = isFile
		? node.file?.icon
		: open
			? 'catppuccin:folder-open'
			: 'catppuccin:folder';
	const isActive = isFile && activePath === node.file!.path;

	return (
		<>
			<button
				type="button"
				onClick={() =>
					isFile ? onSelect(node.file!.path) : setOpen((o) => !o)
				}
				className={cn(
					'flex w-full items-center gap-1.5 rounded px-1.5 py-1 text-left font-mono text-sm transition',
					'hover:bg-muted',
					isActive && 'bg-muted text-foreground',
					!isActive && 'text-muted-foreground',
				)}
				style={{ paddingLeft: `${depth * 12 + 6}px` }}
			>
				{iconName && <Iconify icon={iconName} className="size-3.5 shrink-0" />}
				<span className="truncate">{node.name}</span>
			</button>
			{!isFile && open && (
				<Tree
					node={node}
					activePath={activePath}
					onSelect={onSelect}
					depth={depth + 1}
				/>
			)}
		</>
	);
}

export function CodeEditor({
	children,
	title,
}: {
	children?: ReactNode;
	title?: string;
}) {
	const files = useMemo<FileEntry[]>(
		() => parseFileEntries(children, { requireFilename: true }),
		[children],
	);

	const [activePath, setActivePath] = useState(files[0]?.path ?? '');
	const [openTabs, setOpenTabs] = useState<string[]>(
		files[0] ? [files[0].path] : [],
	);

	const tree = useMemo(
		() => (!files.length ? undefined : buildTree(files)),
		[files],
	);

	if (!files.length) return null;
	const active = files.find((f) => f.path === activePath) ?? files[0];

	const openFile = (path: string) => {
		setActivePath(path);
		setOpenTabs((tabs) => (tabs.includes(path) ? tabs : [...tabs, path]));
	};

	const closeTab = (e: React.MouseEvent, path: string) => {
		e.stopPropagation();
		const next = openTabs.filter((tab) => tab !== path);
		setOpenTabs(next);
		if (path === activePath && next.length) {
			setActivePath(next[next.length - 1]);
		}
	};

	return (
		<CodeBlockFrame
			className="my-6"
			header={
				<>
					<MacosAction className="-mr-3 mb-0 origin-left scale-75" />
					<span className="ms-2 font-mono text-muted-foreground text-xs">
						~/{title ?? 'sohan/code'}
					</span>
				</>
			}
			actions={
				files.length > 0 ? (
					<CopyButton
						className="ml-auto mr-1.5"
						text={getCodeText(active.node)}
					/>
				) : undefined
			}
		>
			<div className="grid grid-cols-1 md:grid-cols-[180px_1fr]">
				{/* Sidebar */}
				{tree && (
					<aside className="hidden h-[50vh] min-h-0 overflow-y-auto overscroll-auto border-border border-e bg-muted/30 p-2 md:block">
						<div className="mb-2 px-1.5 font-semibold text-3xs text-muted-foreground uppercase tracking-wider">
							Explorer
						</div>
						<Tree node={tree} activePath={activePath} onSelect={openFile} />
					</aside>
				)}

				{/* Editor */}
				<div className="flex h-[50vh] min-h-0 min-w-0 flex-col">
					{/* Tabs — mobile: all files, desktop: opened tabs */}
					<div className="scrollbar-thin scrollbar-thumb-secondary flex overflow-x-auto border-border border-b bg-muted/20">
						{/* Mobile: show all files as flat tabs */}
						{files.map((f) => (
							<FileTab
								key={f.path}
								icon={f.icon}
								active={f.path === activePath}
								onClick={() => openFile(f.path)}
								className="md:hidden"
							>
								{f.name}
							</FileTab>
						))}
						{/* Desktop: show opened tabs with close buttons */}
						{openTabs.map((path) => {
							const f = files.find((x) => x.path === path);
							if (!f) return null;
							return (
								<FileTab
									key={path}
									icon={f.icon}
									active={path === activePath}
									onClick={() => setActivePath(path)}
									onKeyDown={() => setActivePath(path)}
									className="max-md:hidden"
								>
									<span className="font-mono">{f.name}</span>
									<span
										onClick={(e) => closeTab(e, path)}
										onKeyDown={(e) => e.stopPropagation()}
										onKeyUp={(e) => e.stopPropagation()}
										className={cn(
											'ms-1 rounded opacity-60 transition hover:opacity-100',
											path !== activePath && 'invisible',
										)}
									>
										<Iconify icon="lucide:x" className="size-3" />
									</span>
								</FileTab>
							);
						})}
					</div>

					{/* Active file */}
					<div className="min-h-0 min-w-0 flex-1 overflow-auto overscroll-auto">
						{active && (
							<Pre {...elementProps(active.node)} showLineNumbers hideHeader>
								{elementChildren(active.node)}
							</Pre>
						)}
					</div>
				</div>
			</div>
		</CodeBlockFrame>
	);
}

// Simpler horizontal-tabs variant for :::tabs
export function CodeTabs({ children }: { children?: ReactNode }) {
	const files = useMemo<FileEntry[]>(
		() => parseFileEntries(children),
		[children],
	);

	const [active, setActive] = useState(files[0]?.path ?? '');
	if (!files.length) return null;
	const cur = files.find((f) => f.path === active) ?? files[0];

	return (
		<div className="corner-bevel my-4 overflow-hidden rounded border border-border bg-card shadow-sm">
			<div className="scrollbar-thin scrollbar-thumb-secondary flex items-center overflow-x-auto border-border border-b bg-muted/40">
				{files.map((f) => (
					<FileTab
						key={f.path}
						icon={f.icon}
						active={f.path === active}
						onClick={() => setActive(f.path)}
					>
						{f.name}
					</FileTab>
				))}
				<CopyButton className="ml-auto mr-1.5" text={getCodeText(cur.node)} />
			</div>

			<Pre {...elementProps(cur.node)} hideHeader>
				{elementChildren(cur.node)}
			</Pre>
		</div>
	);
}
